"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getProcessingFeeByPaymentIntentId = void 0;
exports.policyConditionApplies = policyConditionApplies;
exports.generateLineItem = generateLineItem;
exports.generateSubtotalAdjLineItem = generateSubtotalAdjLineItem;
exports.calculateAmountsFromInvoice = calculateAmountsFromInvoice;
exports.generateInvoice = generateInvoice;
exports.generatePayoutInvoice = generatePayoutInvoice;
exports.generateTaxInvoices = generateTaxInvoices;
exports.generateStripeFeeInvoice = generateStripeFeeInvoice;
exports.generateRefundInvoice = generateRefundInvoice;
exports.generateNoShowFeeInvoice = generateNoShowFeeInvoice;
exports.priceItemAtTime = priceItemAtTime;
exports.customerInvoice = customerInvoice;
const TDR_1 = require("../types/TDR");
const time_1 = require("./time");
const reservation_1 = require("./reservation");
const phone_1 = require("./phone");
const firestore_1 = require("./firestore");
const bookingType_1 = require("./bookingType");
const pick_1 = __importDefault(require("lodash/pick"));
const flatMap_1 = __importDefault(require("lodash/flatMap"));
const toPairs_1 = __importDefault(require("lodash/toPairs"));
const maxBy_1 = __importDefault(require("lodash/maxBy"));
const groupBy_1 = __importDefault(require("lodash/groupBy"));
const map_1 = __importDefault(require("lodash/map"));
const intersection_1 = __importDefault(require("lodash/intersection"));
const filter_1 = __importDefault(require("lodash/filter"));
const isEmpty_1 = __importDefault(require("lodash/isEmpty"));
const sortBy_1 = __importDefault(require("lodash/sortBy"));
const reduce_1 = __importDefault(require("lodash/reduce"));
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
const isArray_1 = __importDefault(require("lodash/isArray"));
const luxon_1 = require("luxon");
const numerical_1 = require("./numerical");
var ItemType = TDR_1.TDR.Reservation.ItemType;
function policyConditionApplies(condition, target) {
    var _a, _b, _c, _d, _e;
    if (!condition || (0, isEmpty_1.default)(condition)) {
        return true;
    }
    if (condition.and) {
        if (!condition.and.every(clause => policyConditionApplies(clause, target))) {
            return false;
        }
    }
    if (condition.or) {
        if (!condition.or.some(clause => policyConditionApplies(clause, target))) {
            return false;
        }
    }
    if (condition.not) {
        if (policyConditionApplies(condition.not, target)) {
            return false;
        }
    }
    // nice and sloppy, just how I like it
    const { reservation, item, invoice } = target;
    if (!item) {
        if (condition.itemType || condition.itemId) {
            return false;
        }
        if (condition.hasItem) {
            const hasMatchingItem = (reservation.items || []).some(item => ((!condition.hasItem.id || item.id === condition.hasItem.id) &&
                (!condition.hasItem.type || item.type === condition.hasItem.type)));
            if (!hasMatchingItem) {
                return false;
            }
        }
        if (condition.hasTag) {
            const cHasTags = (0, isArray_1.default)(condition.hasTag) ? condition.hasTag : [condition.hasTag];
            const hasMatchingItemTag = (reservation.items || []).some(item => !(0, isEmpty_1.default)((0, intersection_1.default)(item.tags || [], cHasTags)));
            if (!hasMatchingItemTag) {
                return false;
            }
        }
    }
    // yes, there's an item
    else {
        if (condition.itemType && condition.itemType !== item.type) {
            return false;
        }
        if (condition.itemId && condition.itemId !== item.id) {
            return false;
        }
        if (condition.tag) {
            const cTags = (0, isArray_1.default)(condition.tag) ? condition.tag : [condition.tag];
            if (!item || (0, isEmpty_1.default)((0, intersection_1.default)(item.tags || [], cTags))) {
                return false;
            }
        }
    }
    const time = (0, time_1.toLuxon)(reservation.time, reservation.timezone);
    if (condition.betweenTimes) {
        const [stime, etime] = condition.betweenTimes;
        if (!(0, time_1.withinTimeRange)(time, stime, etime)) {
            return false;
        }
    }
    if (condition.betweenGuests) {
        const [min, max] = condition.betweenGuests;
        if (reservation.guests < min || reservation.guests > max) {
            return false;
        }
    }
    if (condition.daysOfWeek && !(0, time_1.withinWeekdays)(time, condition.daysOfWeek)) {
        return false;
    }
    if ((_a = condition.daysOfMonth) === null || _a === void 0 ? void 0 : _a.length) {
        const dom = time.day;
        if (!condition.daysOfMonth.includes(dom)) {
            return false;
        }
    }
    // a list of dates, in one of the following formats, with or without leading zeroes ('en-us' locale)
    // (for tokens, see: https://moment.github.io/luxon/#/formatting?id=table-of-tokens )
    // 'MMMM dd, yyyy'
    // 'MMMM dd'
    // 'MMMM'
    // 'yyyy-MM-dd'
    // 'MM-dd'
    if (condition.dates) {
        if (!(0, time_1.someDateMatches)(time, condition.dates)) {
            return false;
        }
    }
    // an array of two dates, in 'yyyy-MM-dd' or 'MM-dd' format
    if (condition.betweenDates) {
        if (!(0, time_1.betweenDates)(time, condition.betweenDates)) {
            return false;
        }
    }
    // minimum hours before reservation time
    if (condition.leadTimeHours) {
        const now = luxon_1.DateTime.now();
        const hoursUntilReservation = time.diff(now, 'hours').hours;
        if (hoursUntilReservation < condition.leadTimeHours) {
            return false;
        }
    }
    if (((_b = condition.guestContact) === null || _b === void 0 ? void 0 : _b.phone) && (0, phone_1.formatPhoneNumber)(reservation.phone) !== (0, phone_1.formatPhoneNumber)((_c = condition.guestContact) === null || _c === void 0 ? void 0 : _c.phone)) {
        return false;
    }
    if (((_d = condition.guestContact) === null || _d === void 0 ? void 0 : _d.email) && reservation.email !== ((_e = condition.guestContact) === null || _e === void 0 ? void 0 : _e.email)) {
        return false;
    }
    if (condition.toAccount) {
        if (!(0, toPairs_1.default)(condition.toAccount).every(([key, val]) => { var _a; return ((_a = invoice === null || invoice === void 0 ? void 0 : invoice.toAccount) === null || _a === void 0 ? void 0 : _a[key]) === val; })) {
            return false;
        }
    }
    return true;
}
function applicablePolicies(policies, target) {
    // if we ever get away from legacy timeslot pricing, this code can revert to the slower, more legible variant that exists in the history (circa 2021-11-17)
    const targetTargetType = target.item ? 'item' : 'subtotal';
    const applicablePolicies = [];
    for (let i = policies.length - 1; i >= 0; i--) {
        const policy = policies[i];
        if ((policy.targetType || 'item') === targetTargetType &&
            policyConditionApplies(policy.condition, target)) {
            applicablePolicies.unshift(policy);
            if (policy.override) {
                break;
            }
        }
    }
    return applicablePolicies;
}
function extPolicyCharges(policy) {
    return policy.charges.map(charge => (Object.assign(Object.assign({}, charge), { label: charge.label || policy.name, amount: charge.amount || 0, policyId: policy.id })));
}
function applyCharge(lineItem, charge, targets) {
    var _a, _b, _c, _d;
    const targetKey = charge.target || TDR_1.TDR.PricingPolicy.Charge.DEFAULT_TARGET;
    let targetVal = lineItem[targetKey] || 0;
    let absAmount = 0;
    switch (charge.type) {
        case 'add':
            absAmount = charge.amount;
            break;
        // there's some obvious ways to generalize this, but I'm convinced that it would make the code AND the data harder to understand.
        // this way, the jank is explicit and localized, and there's a clear place to add any NEW jank that comes along
        case 'multiply':
            absAmount = targetVal * charge.amount;
            break;
        case 'per-guest':
            const guests = ((_a = targets === null || targets === void 0 ? void 0 : targets.item) === null || _a === void 0 ? void 0 : _a.guests) || ((_b = targets === null || targets === void 0 ? void 0 : targets.reservation) === null || _b === void 0 ? void 0 : _b.guests) || 0;
            absAmount = (guests * charge.amount);
            break;
        case 'multiply-subtotal':
            const subtotal = ((_c = targets === null || targets === void 0 ? void 0 : targets.invoice) === null || _c === void 0 ? void 0 : _c.subtotal) || 0;
            absAmount = (subtotal * charge.amount);
            break;
        case 'add-per-quantity': {
            if (!charge.itemId) {
                absAmount = 0;
                break;
            }
            const matched = (((_d = targets === null || targets === void 0 ? void 0 : targets.reservation) === null || _d === void 0 ? void 0 : _d.items) || []).find(i => i.id === charge.itemId);
            const qty = (matched === null || matched === void 0 ? void 0 : matched.quantity) || 0;
            absAmount = qty * charge.amount;
            break;
        }
    }
    targetVal = targetVal + absAmount;
    const charges = [...lineItem.charges, Object.assign(Object.assign({}, charge), { absAmount })];
    // console.log('APPLY CHARGE', absAmount, lineItem, charge, targets);
    return Object.assign(Object.assign({}, lineItem), { charges, [targetKey]: targetVal });
}
function generateLineItem(policies, targets, seed) {
    var _a, _b, _c;
    const charges = (0, flatMap_1.default)(applicablePolicies(policies, targets), extPolicyCharges);
    const lineItem = Object.assign({ charges: [], total: 0, deposit: 0, deferred: 0, item: Object.assign(Object.assign({}, ((targets === null || targets === void 0 ? void 0 : targets.item) || {})), { guests: (_b = (_a = targets === null || targets === void 0 ? void 0 : targets.item) === null || _a === void 0 ? void 0 : _a.guests) !== null && _b !== void 0 ? _b : (_c = targets === null || targets === void 0 ? void 0 : targets.reservation) === null || _c === void 0 ? void 0 : _c.guests }) }, (seed || {}));
    return (0, reduce_1.default)(charges, (lineItem, charge) => applyCharge(lineItem, charge, targets), lineItem);
}
// thanks, I hate it
function generateSubtotalAdjLineItem(policy, targets, subtotal) {
    const charges = (0, flatMap_1.default)(applicablePolicies([policy], targets), extPolicyCharges);
    let lineItem = {
        charges: [],
        total: subtotal,
        tax: policy.tax
    };
    if (!lineItem.tax) {
        delete lineItem.tax;
    }
    lineItem = (0, reduce_1.default)(charges, (lineItem, charge) => applyCharge(lineItem, charge, targets), lineItem);
    // the trouble is, this gives the total WITH the charge, but I want the total OF the charge
    // this is a hilarious hack that should be re-examined at some point, but it will do for now
    lineItem.total = lineItem.total - subtotal;
    return lineItem;
}
function ccStatement(reservation, restaurantName) {
    const resTime = (0, time_1.toLuxon)(reservation.time, reservation.timezone);
    return (`${resTime === null || resTime === void 0 ? void 0 : resTime.toFormat('LL-dd')} ${restaurantName}`).replace(/\s+/, ' ').trim().slice(0, 22);
}
function generateLineItemFromPromoCode(promoCode, lineItems) {
    const prevTotal = lineItems.reduce((subtotal, lineItem) => subtotal + lineItem.total, 0);
    const promoCodeDiscount = promoCode.discountType === 'flat' ? promoCode.discountAmount : prevTotal * (promoCode.discountAmount / 100);
    return {
        item: {
            type: ItemType.Coupon,
            id: promoCode.code,
            subtotal: promoCodeDiscount
        },
        charges: [{
                type: promoCode.discountType === 'flat' ? 'add' : 'multiply-subtotal',
                amount: promoCode.discountAmount
            }],
        total: -Math.min(prevTotal, promoCodeDiscount, promoCode.maximumDiscountAmount || Number.MAX_VALUE)
    };
}
function calculateAmountsFromInvoice(invoice) {
    if (invoice.type !== 'reservation' && invoice.type !== 'noshow-fee') {
        throw new Error(`Cannot calculate amounts from invoice of type ${invoice.type}`);
    }
    const totalBeforeDiscount = invoice.lineItems.reduce((subtotal, lineItem) => {
        if (lineItem.item.type !== ItemType.Coupon) {
            return subtotal + lineItem.total;
        }
        return subtotal;
    }, 0);
    const promoCodeAmount = invoice.lineItems.reduce((discount, lineItem) => {
        if (lineItem.item.type === ItemType.Coupon) {
            return discount + Math.abs(lineItem.total);
        }
        return discount;
    }, 0);
    const taxLineItems = invoice.subtotalPriceAdjustments.reduce((acc, p) => {
        acc[p.tax] = p.total;
        return acc;
    }, {});
    return {
        totalBeforeDiscount,
        promoCodeAmount,
        taxLineItems
    };
}
function generateInvoice(currency, policies, reservation, prevInvoice, restaurant, prevReservation, tableInfo, promoCode) {
    var _a;
    //TODO handle items, no tax
    // Keep LGB Deposit policies only for request-booked tables. Booking type comes from
    // getBookingType, which prefers `bookingType` and falls back to the deprecated
    // supportLargeGroup — so an absent tableInfo prices as 'instant' and drops the deposit.
    if (!(0, bookingType_1.requiresRequestFlow)(tableInfo)) {
        policies = policies.filter((policy) => policy.name !== TDR_1.TDR.LARGE_GROUP_DEPOSIT_POLICY_NAME);
    }
    const cutoffs = restaurant && prevReservation && (0, reservation_1.computeCutoffs)(prevReservation, restaurant);
    const expiredCutoff = cutoffs && cutoffs.cancel < luxon_1.DateTime.local();
    if (prevInvoice) {
        if (reservation.lockedMinimumPrice) {
            reservation.lockedMinimumPrice = Math.min(reservation.lockedMinimumPrice, prevInvoice.subtotal); //handles case with free reservations and deposits
        }
        if (expiredCutoff) {
            reservation.lockedMinimumPrice = prevInvoice.subtotal;
        }
    }
    policies = (0, sortBy_1.default)([...policies], p => p.priority);
    const mergedAddons = [];
    const addonIndexByKey = new Map();
    for (const item of reservation.items.filter(i => i.type === ItemType.AddOn)) {
        const key = `${item.id}::${item.variantId || ''}`;
        const existingIndex = addonIndexByKey.get(key);
        if (existingIndex !== undefined) {
            const existing = mergedAddons[existingIndex];
            existing.quantity = (existing.quantity || 0) + (item.quantity || 0);
        }
        else {
            addonIndexByKey.set(key, mergedAddons.length);
            mergedAddons.push(Object.assign({}, item));
        }
    }
    const nonAddonItems = reservation.items.filter(i => i.type !== ItemType.AddOn);
    reservation = Object.assign(Object.assign({}, reservation), { items: [...nonAddonItems, ...mergedAddons] });
    let lineItems = reservation.items.filter(item => item.type !== ItemType.Coupon && item.type !== ItemType.AddOn)
        .map(item => generateLineItem(policies, { reservation, item }))
        .filter(item => !(0, isEmpty_1.default)(item.charges));
    if (promoCode) {
        const promoCodeLineItem = generateLineItemFromPromoCode(promoCode, lineItems);
        lineItems = [...lineItems, promoCodeLineItem];
    }
    const addonLineItems = mergedAddons.map(item => {
        const basePrice = (0, numerical_1.toNearestCent)((item.price || 0) * (item.quantity || 0));
        const baseCharge = {
            type: 'add',
            amount: basePrice,
            target: 'total',
            label: 'addon base price',
            policyId: null
        };
        const seed = {
            item,
            charges: [baseCharge],
            total: basePrice,
            deferred: 0
        };
        return generateLineItem(policies, { reservation, item }, seed);
    });
    const lockedMinimum = reservation.lockedMinimumPrice || 0;
    const totalBeforeDiscount = (0, numerical_1.toNearestCent)(lineItems.reduce((subtotal, lineItem) => subtotal + lineItem.total, 0));
    let discountAmount = 0;
    let subtotal = totalBeforeDiscount;
    if (!!(tableInfo === null || tableInfo === void 0 ? void 0 : tableInfo.partnershipName)) {
        subtotal = Math.max((0, numerical_1.calculatePriceAfterDiscount)(totalBeforeDiscount, tableInfo === null || tableInfo === void 0 ? void 0 : tableInfo.partnershipDiscountType, tableInfo === null || tableInfo === void 0 ? void 0 : tableInfo.partnershipDiscountAmount), lockedMinimum);
        discountAmount = (0, numerical_1.toNearestCent)(totalBeforeDiscount - subtotal);
    }
    else {
        subtotal = Math.max(totalBeforeDiscount, lockedMinimum);
    }
    const subtotalAdjustmentLineItems = policies
        .map(policy => generateSubtotalAdjLineItem(policy, { reservation, invoice: { subtotal } }, subtotal)) // that invoice is NOT quite right
        .filter(item => !(0, isEmpty_1.default)(item.charges));
    // console.log('SUBTOTAL ITEMS', subtotalAdjustmentLineItems);
    const subtotalAdjustment = subtotalAdjustmentLineItems.reduce((adj, lineItem) => adj + (lineItem.total || 0), 0);
    const depositAdjustment = [...lineItems, ...addonLineItems].reduce((adj, lineItem) => adj + (lineItem.deposit || 0), 0);
    const depositAdjustmentWithMinimum = Math.max(expiredCutoff && prevInvoice ? Math.max(depositAdjustment, prevInvoice.deposit) : depositAdjustment, (reservation.depositMinimumPrice || 0));
    const total = (0, numerical_1.toNearestCent)(subtotal + subtotalAdjustment + depositAdjustmentWithMinimum);
    const payable = (0, numerical_1.toNearestCent)(total - ((prevInvoice === null || prevInvoice === void 0 ? void 0 : prevInvoice.total) || 0));
    let capturable = 0;
    if (!prevInvoice || prevInvoice.status === 'paid') {
        capturable = Math.max(payable, 0);
    }
    else {
        capturable = Math.max(prevInvoice.capturable + payable, 0);
    }
    return {
        description: ccStatement(reservation, restaurant.name),
        type: 'reservation',
        status: 'pending',
        exchangeRate: (prevInvoice === null || prevInvoice === void 0 ? void 0 : prevInvoice.exchangeRate) || 1,
        toAccount: { type: TDR_1.TDR.Invoice.PartyType.TK, id: null, region: ((_a = prevInvoice === null || prevInvoice === void 0 ? void 0 : prevInvoice.toAccount) === null || _a === void 0 ? void 0 : _a.region) || (restaurant === null || restaurant === void 0 ? void 0 : restaurant.stripeRegion) },
        toAccountType: TDR_1.TDR.Invoice.PartyType.TK,
        fromAccount: { type: TDR_1.TDR.Invoice.PartyType.USER, id: reservation.userId },
        realized: false,
        reservationId: reservation.id,
        restaurantId: reservation.restaurantId,
        modifiedFrom: prevInvoice,
        paymentIntentId: prevInvoice && prevInvoice.status !== 'paid' && capturable > 0 && payable <= 0 ? prevInvoice.paymentIntentId : null,
        lineItems: [...lineItems, ...addonLineItems],
        subtotal: subtotal,
        subtotalPriceAdjustments: subtotalAdjustmentLineItems,
        lockedMinimum,
        currency,
        total,
        deposit: depositAdjustmentWithMinimum,
        payable,
        capturable: (0, numerical_1.toNearestCent)(capturable),
        discountAmount: discountAmount,
        discountOrigin: tableInfo === null || tableInfo === void 0 ? void 0 : tableInfo.partnershipName
    };
}
//currently could just set it to zero instead of throwing errors, but in the future we may want to differentiate between an  error and no stripe fee.
const getProcessingFeeByPaymentIntentId = (firestore, paymentIntentId) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    if (!firestore || !paymentIntentId) {
        throw new Error('firestore is not defined');
    }
    try {
        const stripeFeeInvoice = (0, firestore_1.querySnapshotToArray)(yield firestore.collection('Invoices')
            .where('type', '==', 'stripe')
            .where('paymentIntentId', '==', paymentIntentId)
            .limit(1)
            .get());
        return ((_a = stripeFeeInvoice === null || stripeFeeInvoice === void 0 ? void 0 : stripeFeeInvoice[0]) === null || _a === void 0 ? void 0 : _a.total) ? stripeFeeInvoice[0].total : 0;
    }
    catch (e) {
        throw e;
    }
});
exports.getProcessingFeeByPaymentIntentId = getProcessingFeeByPaymentIntentId;
// part of the trouble here is, most of this machinery is about _reservations_, and I want to be thinking about *invoices*
// if we say that payouts are calculated with pricing policies (and I think we DO want to say that)
// then they inherit all sorts of facts about reservations that don't feel entirely relevant
// maybe we just want a different concept?
// lots of what policyConditions care about don't feel super relevant, after all ...
// well, hang on a minute - is that true? like, what DO we care about?
// maybe we want to pay out differently based on group size? that's a fact about the reservation
// maybe we want to notice specific promotions? reservation again
// maybe we want to say things about the TOTAL cost? (ie, 10% or $1, whichever is greater) - THIS ONE IS THE INVOICE
// okay, there's a good argument for generalizing the machinery a little, but ALSO for rolling the reservation into the process ...
function generatePayoutInvoice(policies, reservation, restaurant, customerInvoice, noShowInvoices, stripeInvoices, paidRestaurantInvoices
//taxInvoices: TDR.Invoice<T>[] = [] //for cases where we let the restaurant handle the tax (Eg. UAE)
) {
    // if I've been burned by anything here, it's trying to be too clever guessing what's gonna be required
    // so, I'm NOT going to try to price individual lineitems - I'm JUST going to make one lineItem directly from each invoice
    var _a;
    //TODO handle items and different payout percentages for each
    policies = (0, sortBy_1.default)([...policies], p => p.priority);
    const lineItems = [customerInvoice, ...stripeInvoices].map(invoice => {
        const item = {
            type: TDR_1.TDR.Reservation.ItemType.Invoice,
            id: invoice.id,
            subtotal: invoice.subtotal
        };
        const generatedItem = generateLineItem(policies, { reservation, invoice, item });
        if (invoice.exchangeRate) {
            return Object.assign(Object.assign({}, generatedItem), { exchangeRate: invoice.exchangeRate, total: generatedItem.total * invoice.exchangeRate });
        }
        else {
            return generatedItem;
        }
    });
    const exchangeRate = customerInvoice.exchangeRate || 1;
    //so we eventually want these to be apart of the lineItems, with their own pricing policies applied to the (same with deposits), but for now this is ok
    const noShowTotal = (0, numerical_1.toNearestCent)(noShowInvoices.reduce((total, invoice) => total + (invoice.total * exchangeRate || 0), 0));
    //dont payout tax for now
    // const taxTotal = toNearestCent(taxInvoices?.reduce((total, invoice) => total + (invoice.total * exchangeRate || 0), 0)) || 0;
    // skipping subtotal adjustments for now because it's not specifically required, and things aren't quite fitting right
    // THIS IS A CODE SMELL - I keep waiting for this code to snap into focus, and cut away all the cruft, and it keeps not happening
    // I think the problem is that I'm trying to fold in the lineItem generation - maybe I need to separate that out?
    const deposit = (customerInvoice === null || customerInvoice === void 0 ? void 0 : customerInvoice.deposit) ? customerInvoice.deposit * exchangeRate : 0;
    const total = (0, numerical_1.toNearestCent)(lineItems.reduce((subtotal, lineItem) => subtotal + (lineItem.total || 0), 0) + deposit + noShowTotal);
    const paid = paidRestaurantInvoices.reduce((sum, { total }) => sum + total, 0);
    const payable = total - paid;
    const timeStr = (_a = (0, time_1.toLuxon)(reservation.time)) === null || _a === void 0 ? void 0 : _a.toFormat('LL-dd T');
    return {
        description: `tablz payout: ${reservation.tableName} ${timeStr}`,
        type: 'payout',
        status: 'pending',
        // in order to nudge legacy data back into alignment, most of this info will probably be overwritten on payment
        // for example, invoices where the fromAccount and the toAccount don't share a region, or invoices created before the restaurant had a stripeAccount
        fromAccount: customerInvoice.toAccount,
        toAccount: { type: TDR_1.TDR.Invoice.PartyType.RESTAURANT, id: restaurant.accountId, region: restaurant.stripeRegion },
        toAccountType: TDR_1.TDR.Invoice.PartyType.RESTAURANT,
        parentInvoiceId: customerInvoice.id,
        currency: getPayoutCurrency(customerInvoice),
        payoutBreakdown: {
            noShowTotal,
            deposit
        },
        lineItems: lineItems,
        subtotal: total,
        subtotalPriceAdjustments: [],
        total,
        payable,
        reservationId: reservation.id,
        restaurantId: restaurant.id
    };
}
function getPayoutCurrency(customerInvoice) {
    return (customerInvoice === null || customerInvoice === void 0 ? void 0 : customerInvoice.currency)
        ? (customerInvoice.currency === TDR_1.TDR.Currency.AED || customerInvoice.currency === TDR_1.TDR.Currency.EUR) ? TDR_1.TDR.Currency.USD : customerInvoice.currency
        : TDR_1.TDR.DEFAULT_CURRENCY;
}
function generateTaxInvoices(invoice) {
    return (0, map_1.default)(
    // aggregate tax lineItems by tax-id (shouldn't matter, there should really only be one line-item per tax)
    (0, toPairs_1.default)((0, groupBy_1.default)(invoice.subtotalPriceAdjustments.filter(adj => adj.tax), adj => adj.tax)), ([tax, lineItems]) => {
        const total = (0, numerical_1.toNearestCent)(lineItems.reduce((total, adj) => total + adj.total, 0));
        return {
            type: 'tax',
            parentInvoiceId: invoice.id,
            status: 'pending',
            fromAccount: invoice.toAccount,
            toAccount: { type: TDR_1.TDR.Invoice.PartyType.TAX, id: tax },
            toAccountType: TDR_1.TDR.Invoice.PartyType.TAX,
            lineItems,
            subtotal: total,
            subtotalPriceAdjustments: [],
            lockedMinimum: 0,
            total,
            payable: total,
            currency: invoice.currency || TDR_1.TDR.DEFAULT_CURRENCY,
            reservationId: invoice.reservationId,
            restaurantId: invoice.restaurantId
        };
    });
}
function generateStripeFeeInvoice(reservation, invoice, transaction) {
    const parentInvoiceId = transaction.description;
    const amount = (0, numerical_1.toNearestCent)(transaction.fee * 0.01); // stripe amounts are in pennies
    return {
        type: 'stripe',
        status: 'paid',
        toAccount: { type: TDR_1.TDR.Invoice.PartyType.STRIPE, id: null },
        toAccountType: TDR_1.TDR.Invoice.PartyType.STRIPE,
        fromAccount: { type: TDR_1.TDR.Invoice.PartyType.TK, id: transaction.tkAccount, region: transaction.region },
        paymentIntentId: invoice.paymentIntentId,
        reservationId: reservation.id,
        restaurantId: reservation.restaurantId,
        parentInvoiceId,
        lineItems: [{
                item: { type: TDR_1.TDR.Reservation.ItemType.StripeFee, id: transaction.id },
                charges: [{ type: 'add', amount: transaction.fee, label: 'stripe fee', policyId: null }],
                total: amount,
                deferred: 0
            }],
        subtotal: amount,
        subtotalPriceAdjustments: [],
        total: amount,
        payable: amount,
        lockedMinimum: 0,
        currency: transaction.currency || TDR_1.TDR.DEFAULT_CURRENCY
    };
}
function generateRefundInvoice(invoice, subtotalAmount, depositAmount, reservation, subtotalPolicies) {
    const refundInvoice = (0, cloneDeep_1.default)(invoice);
    depositAmount = Math.abs(depositAmount);
    subtotalAmount = Math.abs(subtotalAmount);
    refundInvoice.lineItems.push({
        item: { type: TDR_1.TDR.Reservation.ItemType.Refund, id: null },
        charges: [{ policyId: 'admin-override-refund', type: 'add', label: 'refund', amount: -subtotalAmount }],
        total: -subtotalAmount,
        deposit: -depositAmount,
        deferred: 0
    });
    const subtotal = (0, numerical_1.toNearestCent)(Math.max(refundInvoice.lineItems
        .filter(lineItem => lineItem.item.type !== ItemType.AddOn)
        .reduce((subtotal, lineItem) => subtotal + lineItem.total, 0), 0));
    const deposit = (0, numerical_1.toNearestCent)(Math.max(refundInvoice.lineItems
        .filter(lineItem => lineItem.item.type !== ItemType.AddOn)
        .reduce((deposit, lineItem) => deposit + (lineItem.deposit ? lineItem.deposit : 0), 0), 0));
    const subtotalAdjustmentLineItems = subtotalPolicies
        .map(policy => generateSubtotalAdjLineItem(policy, { reservation, invoice: { subtotal } }, subtotal)) // that invoice is NOT quite right
        .filter(item => !(0, isEmpty_1.default)(item.charges));
    const subtotalAdjustment = (0, numerical_1.toNearestCent)(subtotalAdjustmentLineItems.reduce((adj, lineItem) => adj + (lineItem.total || 0), 0));
    const adjustedTotal = subtotal + subtotalAdjustment + deposit;
    const total = (0, numerical_1.toNearestCent)(adjustedTotal);
    const payable = (0, numerical_1.toNearestCent)(total - invoice.total);
    let capturable = 0;
    if (!invoice || invoice.status === 'paid') {
        capturable = Math.max(payable, 0);
    }
    else {
        capturable = Math.max(invoice.capturable + payable, 0);
    }
    refundInvoice.id = null;
    refundInvoice.description = `REFUND ${invoice.description}`;
    refundInvoice.exchangeRate = invoice.exchangeRate;
    refundInvoice.createdAt = null;
    refundInvoice.status = 'pending';
    refundInvoice.modifiedFrom = invoice;
    refundInvoice.subtotal = subtotal;
    refundInvoice.deposit = deposit;
    refundInvoice.subtotalPriceAdjustments = subtotalAdjustmentLineItems;
    refundInvoice.total = total;
    refundInvoice.lockedMinimum = 0;
    refundInvoice.capturable = (0, numerical_1.toNearestCent)(capturable);
    refundInvoice.payable = payable;
    refundInvoice.paymentIntentId = invoice && invoice.status !== 'paid' && capturable > 0 && payable <= 0 ? invoice.paymentIntentId : null;
    return refundInvoice;
}
// mmkay, this should be where we figure out the taxes
function generateNoShowFeeInvoice(resInvoice, amount, subtotalPolicies) {
    // with the given taxes (subtotalPolicies), what subtotal will arrive at the target amount?
    // 		total = (subTotal*1) + (subTotal*tax1) + (subTotal*tax2) ...
    // 		total = subTotal * (1 + tax1 + tax2 ...)
    // 		subTotal = total / (1 + tax1 + tax2 ...)
    // for expedience, I'm going to assume that every tax is composed of only multiply-subtotal charges
    // this is true in practice, but broken in principle HOWEVER, the principles are fucked, and need to be rebuilt
    // so I like works-in-practice, buggy-in-principle code
    const taxPercents = (0, flatMap_1.default)(subtotalPolicies, policy => policy.charges.filter(charge => charge.type === 'multiply-subtotal').map(charge => charge.amount));
    const subtotal = amount / taxPercents.reduce((total, percent) => total + percent, 1);
    const lineItems = [{
            item: { type: TDR_1.TDR.Reservation.ItemType.Refund, id: null },
            charges: [{ policyId: 'admin-override-noshow-fee', type: 'add', label: 'no-show fee', amount: subtotal }],
            total: subtotal,
            deferred: 0
        }];
    const subtotalAdjustmentLineItems = subtotalPolicies
        .map(policy => generateSubtotalAdjLineItem(policy, { invoice: { subtotal } }, subtotal))
        .filter(item => !(0, isEmpty_1.default)(item.charges));
    return Object.assign(Object.assign({ description: `no-show fee: ${resInvoice.description}` }, ((0, pick_1.default)(resInvoice, 'fromAccount', 'toAccount', 'toAccountType', 'restaurantId', 'reservationId', 'currency', 'paymentMethodId', 'paymentIntentId' // I expect this to get overwritten, but it's a hedge for older invoices, that don't know their paymentMethodId
    ))), { type: 'noshow-fee', status: 'pending', lineItems, subtotal: (0, numerical_1.toNearestCent)(subtotal), subtotalPriceAdjustments: subtotalAdjustmentLineItems, lockedMinimum: 0, total: (0, numerical_1.toNearestCent)(amount), payable: (0, numerical_1.toNearestCent)(amount), capturable: (0, numerical_1.toNearestCent)(amount) });
}
// okay, so here's the main thing about this: what something costs *does* potentially vary according to a bunch of factors
// so, we need a way for those factors to be sent in - we'll just slam in a reservation with as much as we know
function priceItemAtTime(datetime, policies, reservation, item) {
    reservation = Object.assign(Object.assign({}, reservation), { time: datetime, items: [item] });
    return generateLineItem(policies, { reservation, item });
}
// this is not a super-clearly-defined concept (I expect that talking about the ONE customer invoice will lose meaning eventually)
// but for now, it'll do
function customerInvoice(invoices) {
    return (0, maxBy_1.default)((0, filter_1.default)(invoices, invoice => (invoice &&
        invoice.type === 'reservation' &&
        invoice.toAccount.type === 'tk' &&
        invoice.fromAccount.type === 'user' &&
        !invoice.modifiedBy &&
        invoice.status !== 'failed')), invoice => { var _a; return ((_a = invoice.createdAt) === null || _a === void 0 ? void 0 : _a.seconds) || 0; });
}
//# sourceMappingURL=invoice.js.map